/**
 * Chunk and leaf node types of the persistent grid (docs/large-volume-plan.md §2.1).
 *
 * `owner` is a transient token: 0 means frozen (shared, never written again); a non-zero token
 * identifies the transient grid that created or copied the node and may still write it in place.
 */
import { CHUNK_CELLS, LEAF_ENTRIES } from './layout'

export type VoxelChunk = {
  /** One Uint16 palette id per cell in linear order ((lz << 8) | (ly << 4) | lx); 0 = empty. */
  readonly data: Uint16Array
  /** Number of non-empty cells. */
  count: number
  /** Transient owner token; 0 = frozen. */
  owner: number
}

export type ChunkLeaf = {
  readonly chunks: (VoxelChunk | null)[]
  owner: number
}

export function createChunk(owner: number): VoxelChunk {
  return { data: new Uint16Array(CHUNK_CELLS), count: 0, owner }
}

export function copyChunk(source: VoxelChunk, owner: number): VoxelChunk {
  return { data: source.data.slice(), count: source.count, owner }
}

export function createLeaf(owner: number): ChunkLeaf {
  return { chunks: new Array<VoxelChunk | null>(LEAF_ENTRIES).fill(null), owner }
}

export function copyLeaf(source: ChunkLeaf, owner: number): ChunkLeaf {
  return { chunks: source.chunks.slice(), owner }
}

/** Recount non-empty cells (after bulk writes to `data`). */
export function recountChunk(chunk: VoxelChunk): number {
  const d = chunk.data
  let n = 0
  for (let i = 0; i < CHUNK_CELLS; i++) if (d[i] !== 0) n++
  chunk.count = n
  return n
}
